/**
 * frontend/src/pages/LoginPage.jsx
 * Signaliq Intelligence - Enterprise auth page with product context for first-time visitors
 *
 * Production theme update:
 * - Adds pre-login light/dark selector through shared ThemeToggle.
 * - Uses ThemeContext so auth page follows the same browser preference as the app.
 * - Removes hardcoded auth-only dark lock by using theme-aware palettes.
 * - Passes Cloudflare Turnstile the active theme.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import {
  BarChart3,
  Brain,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';

const TURNSTILE_SITE_KEY = import.meta.env?.VITE_TURNSTILE_SITE_KEY || '';
const SUPPORT_EMAIL = import.meta.env?.VITE_SUPPORT_EMAIL || 'support@signaliq.ai';
const API_BASE = String(
  import.meta.env?.VITE_API_BASE_URL ||
  import.meta.env?.VITE_API_URL ||
  '/api/v1'
).replace(/\/$/, '');

const DASH_DISPLAY_FONT = "Georgia, 'Times New Roman', serif";
const DASH_BODY_FONT = "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const DASH_MONO_FONT = "'DM Mono', var(--font-mono, 'SFMono-Regular', Consolas, 'Liberation Mono', monospace)";

const AUTH_PALETTE = {
  dark: {
    pageBg: '#060d1a',
    pageBgSoft: '#080f1c',
    gridLine: 'rgba(30,79,216,0.025)',
    glow: 'radial-gradient(ellipse, rgba(20,55,180,0.12) 0%, transparent 70%)',
    panelBg: 'linear-gradient(180deg, rgba(9,20,42,0.76), rgba(7,14,28,0.62))',
    cardBg: 'rgba(8,16,32,0.92)',
    moduleBg: 'rgba(255,255,255,0.035)',
    inputBg: 'rgba(255,255,255,0.035)',
    logoBg: 'linear-gradient(180deg, rgba(0,0,0,0.86) 0%, rgba(3,7,15,0.92) 100%)',
    border: 'rgba(78,128,216,0.13)',
    borderStrong: 'rgba(78,128,216,0.22)',
    textHi: '#f3f7ff',
    text: '#dde6f7',
    textMid: '#8fb0dc',
    textSoft: '#6f88ae',
    textLow: '#5a7a9a',
    muted: '#3d5a7a',
    accent: '#4e80d8',
    accentSoft: '#93c5fd',
    accentBg: 'rgba(30,79,216,0.10)',
    accentBgStrong: 'rgba(30,79,216,0.16)',
    buttonGradient: 'linear-gradient(135deg, #1032a0 0%, #1e4fd8 100%)',
    buttonDisabled: 'rgba(30,79,216,0.35)',
    shadow: '0 20px 70px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
    cardShadow: '0 8px 48px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 0 1px rgba(30,79,216,0.06) inset',
    errorBg: 'rgba(192,25,43,0.09)',
    errorBorder: 'rgba(192,25,43,0.20)',
    error: '#f87171',
    success: '#bbf7d0',
  },
  light: {
    pageBg: '#f4f7fc',
    pageBgSoft: '#ffffff',
    gridLine: 'rgba(37,99,235,0.045)',
    glow: 'radial-gradient(ellipse, rgba(37,99,235,0.13) 0%, transparent 70%)',
    panelBg: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(239,246,255,0.80))',
    cardBg: 'rgba(255,255,255,0.94)',
    moduleBg: 'rgba(37,99,235,0.045)',
    inputBg: '#ffffff',
    logoBg: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.98) 100%)',
    border: 'rgba(15,23,42,0.10)',
    borderStrong: 'rgba(37,99,235,0.20)',
    textHi: '#0f172a',
    text: '#1e293b',
    textMid: '#475569',
    textSoft: '#64748b',
    textLow: '#71809a',
    muted: '#94a3b8',
    accent: '#2563eb',
    accentSoft: '#1d4ed8',
    accentBg: 'rgba(37,99,235,0.08)',
    accentBgStrong: 'rgba(37,99,235,0.12)',
    buttonGradient: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
    buttonDisabled: 'rgba(37,99,235,0.35)',
    shadow: '0 20px 70px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.65)',
    cardShadow: '0 18px 54px rgba(15,23,42,0.13), 0 1px 0 rgba(255,255,255,0.82) inset',
    errorBg: 'rgba(220,38,38,0.08)',
    errorBorder: 'rgba(220,38,38,0.18)',
    error: '#dc2626',
    success: '#15803d',
  },
};

function normalizeTheme(theme) {
  return theme === 'light' ? 'light' : 'dark';
}

function TurnstileWidget({ onToken, onError, themeMode }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    const SCRIPT_ID = 'cf-turnstile-script';

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const render = () => {
      if (!window.turnstile || !containerRef.current) return;

      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // noop
        }
        widgetIdRef.current = null;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: themeMode === 'light' ? 'light' : 'dark',
        callback: (token) => onToken(token),
        'error-callback': () => onError?.('Security challenge failed. Please refresh and try again.'),
        'expired-callback': () => onToken(''),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      window.__onTurnstileReady = render;
      const script = document.getElementById(SCRIPT_ID);
      if (script) script.onload = render;
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // noop
        }
        widgetIdRef.current = null;
      }
    };
  }, [onToken, onError, themeMode]);

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <div
      ref={containerRef}
      style={{ margin: '12px 0 4px', display: 'flex', justifyContent: 'center' }}
      aria-label="Security challenge"
    />
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const themeMode = normalizeTheme(theme);
  const p = AUTH_PALETTE[themeMode];
  const styles = useMemo(() => makeStyles(p), [p]);

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const handleToken = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileError('');
  }, []);

  const handleTsError = useCallback((message) => {
    setTurnstileError(message);
    setTurnstileToken('');
  }, []);

  useEffect(() => {
    document.body.classList.remove('app-shell-lock');
    document.body.classList.add('siq-auth-body');
    document.body.style.background = p.pageBg;
    document.body.style.overflowY = 'auto';

    return () => {
      document.body.classList.remove('siq-auth-body');
      document.body.style.background = '';
      document.body.style.overflowY = '';
    };
  }, [p.pageBg]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setVerificationMessage('');

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the security check before signing in.');
      return;
    }

    setLoading(true);

    try {
      await login(form.email.trim().toLowerCase(), form.password, undefined, {
        cf_turnstile_token: turnstileToken || undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.detail || err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message;

      if (
        detail?.code === 'EMAIL_NOT_VERIFIED' ||
        String(message || err?.message || '').toLowerCase().includes('verify your email')
      ) {
        setVerificationPending(true);
        setVerificationEmail(form.email.trim().toLowerCase());
        setVerificationMessage('Your email has not been verified yet. Please check your inbox for the verification link.');
        setError('');
      } else {
        setError(message || err?.message || 'Login failed - check email and password.');
      }

      if (window.turnstile) {
        try { window.turnstile.reset(); } catch { /* noop */ }
      }

      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    const email = verificationEmail || form.email.trim().toLowerCase();

    if (!email) {
      setError('Enter your email address first, then request a new verification link.');
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/resend-verification/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      let data = {};
      try { data = await response.json(); } catch { /* noop */ }

      if (!response.ok) {
        const detail = data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.message;
        throw new Error(message || 'Unable to resend verification email.');
      }

      setVerificationMessage(data?.message || 'Verification email sent. Please check your inbox and spam folder.');
    } catch (e) {
      setError(e?.message || 'Unable to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  const submitDisabled = loading || Boolean(TURNSTILE_SITE_KEY && !turnstileToken);

  return (
    <div className="auth-page siq-auth-page" style={styles.pageStyle}>
      <ResponsiveAuthStyles />
      <div style={styles.themeToggleWrapStyle}>
        <ThemeToggle />
      </div>

      <div style={styles.gridStyle} />
      <div style={styles.ambientGlowStyle} />

      <main className="siq-auth-layout" style={styles.layoutStyle}>
        <InfoPanel styles={styles} />

        <section className="siq-auth-column" style={styles.authColumnStyle}>
          <div style={styles.brandAreaStyle}>
            <SignalIQCodeLogo styles={styles} />
          </div>

          <div style={styles.cardStyle}>
            <div style={styles.eyebrowStyle}>Secure workspace access</div>
            <div style={styles.titleStyle}>Sign In</div>
            <div style={styles.subtitleStyle}>Access your Signaliq Intelligence workspace, saved analyses, reports, and dashboards.</div>

            {(error || turnstileError) ? <div style={styles.errorStyle}>{error || turnstileError}</div> : null}

            {verificationPending ? (
              <VerificationNotice
                styles={styles}
                message={verificationMessage}
                loading={resendLoading}
                onResend={resendVerificationEmail}
              />
            ) : null}

            <form onSubmit={submit} style={styles.formStyle}>
              <AuthField
                styles={styles}
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@institution.com"
                value={form.email}
                onChange={(value) => setForm((current) => ({ ...current, email: value }))}
              />

              <AuthField
                styles={styles}
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter password"
                value={form.password}
                onChange={(value) => setForm((current) => ({ ...current, password: value }))}
              />

              <TurnstileWidget onToken={handleToken} onError={handleTsError} themeMode={themeMode} />

              <button
                type="submit"
                disabled={submitDisabled}
                style={{
                  ...styles.buttonStyle,
                  cursor: submitDisabled ? 'not-allowed' : 'pointer',
                  background: submitDisabled ? p.buttonDisabled : p.buttonGradient,
                  boxShadow: loading ? 'none' : '0 2px 14px rgba(37,99,235,0.32)',
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={styles.createAccountStyle}>
              New to Signaliq Intelligence? <Link to="/register" style={styles.createAccountLinkStyle}>Create account</Link>
            </div>
          </div>

          <FooterLinks styles={styles} />
        </section>
      </main>
    </div>
  );
}

function InfoPanel({ styles }) {
  const modules = [
    { icon: <TrendingUp size={15} />, title: 'Business Strength & FHI', text: 'Evaluate Business Strength Profile, Financial Health Index, survival probability, benchmarks, covenants, and early-warning signals.' },
    { icon: <BarChart3 size={15} />, title: 'Planning & Decision Analytics', text: 'Run forecasting, scenarios, sensitivity, goal seek, variance, unit economics, investment appraisal, and cost-benefit workflows.' },
    { icon: <Brain size={15} />, title: 'Macro, ESG & AI Reporting', text: 'Monitor macro intelligence, global risk, ESG signals, daily briefings, AI analyst narratives, ingestion, and reports.' },
    { icon: <ShieldCheck size={15} />, title: 'Secure Institutional Workspace', text: 'Protected sign-in, role-aware access, tester onboarding, and administrative controls separated from beta users.' },
  ];

  return (
    <aside className="siq-info-panel" style={styles.infoPanelStyle}>
      <div style={styles.statusPillStyle}><span style={styles.statusDotStyle} /> Early tester workspace</div>
      <h1 style={styles.heroTitleStyle}>Financial intelligence for stronger business decisions.</h1>
      <p style={styles.heroTextStyle}>Signaliq Intelligence brings business strength scoring, financial health analysis, survival probability, forecasting, scenario modeling, benchmarks, risk monitoring, macro intelligence, ESG, ingestion, AI analyst narratives, and reports into one institutional workspace.</p>

      <div style={styles.moduleGridStyle}>
        {modules.map((item) => (
          <div key={item.title} style={styles.moduleCardStyle}>
            <div style={styles.moduleIconStyle}>{item.icon}</div>
            <div>
              <div style={styles.moduleTitleStyle}>{item.title}</div>
              <div style={styles.moduleTextStyle}>{item.text}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.previewStripStyle}>
        <Metric styles={styles} label="Business Strength" value="BSP" />
        <Metric styles={styles} label="Financial Health" value="FHI" />
        <Metric styles={styles} label="Risk Monitoring" value="EWS" />
      </div>
    </aside>
  );
}

function Metric({ styles, label, value }) {
  return (
    <div style={styles.metricStyle}>
      <div style={styles.metricValueStyle}>{value}</div>
      <div style={styles.metricLabelStyle}>{label}</div>
    </div>
  );
}

function VerificationNotice({ styles, message, loading, onResend }) {
  return (
    <div style={styles.verificationStyle}>
      <div style={styles.verificationHeaderStyle}><MailCheck size={16} /> Email Verification Required</div>
      <div style={styles.verificationTextStyle}>{message}</div>
      <div style={styles.verificationActionsStyle}>
        <button type="button" onClick={onResend} disabled={loading} style={styles.secondaryButtonStyle}>
          <RefreshCw size={14} /> {loading ? 'Sending...' : 'Resend Verification'}
        </button>
        <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLinkStyle}>Contact Support</a>
      </div>
    </div>
  );
}

function AuthField({ styles, label, type, autoComplete, placeholder, value, onChange }) {
  return (
    <div>
      <label style={styles.labelStyle}>{label}</label>
      <input
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
        style={styles.inputStyle}
        onFocus={(event) => {
          event.currentTarget.style.borderColor = 'rgba(78,128,216,0.45)';
          event.currentTarget.style.boxShadow = '0 0 0 3px rgba(78,128,216,0.10)';
        }}
        onBlur={(event) => {
          event.currentTarget.style.borderColor = styles.inputStyle.borderColor || styles.inputStyle.border;
          event.currentTarget.style.boxShadow = 'none';
        }}
      />
    </div>
  );
}

function FooterLinks({ styles }) {
  return (
    <>
      <nav aria-label="Legal links" style={styles.legalLinksStyle}>
        <Link to="/privacy" style={styles.legalLinkStyle}>Privacy</Link>
        <Link to="/terms" style={styles.legalLinkStyle}>Terms</Link>
        <Link to="/ai-policy" style={styles.legalLinkStyle}>AI Policy</Link>
        <Link to="/compliance" style={styles.legalLinkStyle}>Compliance</Link>
      </nav>
      <div style={styles.securityTextStyle}>Institutional-grade security | Cloudflare protected | Enterprise authentication</div>
      <div style={styles.copyrightStyle}>Signaliq Intelligence by Nxapredict Analytics LLC</div>
    </>
  );
}

function SignalIQCodeLogo({ styles }) {
  return (
    <div style={styles.logoPlateStyle} aria-label="Signaliq Intelligence">
      <div style={styles.codeBrandStyle}>
        <div style={styles.codeWordWrapStyle}>
          <div style={styles.codeWordStyle}>
            Signaliq
          </div>
          <div style={styles.codeSubtitleStyle}>Intelligence</div>
        </div>
      </div>
    </div>
  );
}

function ResponsiveAuthStyles() {
  return (
    <style>{`
      .siq-auth-page * {
        box-sizing: border-box;
      }

      @media (max-width: 980px) {
        .siq-auth-layout {
          grid-template-columns: 1fr !important;
          max-width: 620px !important;
          gap: 22px !important;
        }

        .siq-info-panel {
          padding: 24px !important;
        }
      }

      @media (max-width: 640px) {
        .siq-auth-page {
          padding: 76px 14px 22px !important;
          align-items: flex-start !important;
        }

        .siq-info-panel {
          padding: 22px 18px !important;
        }

        .siq-auth-column {
          max-width: 100% !important;
        }
      }
    `}</style>
  );
}

function makeStyles(p) {
  const barBaseStyle = {
    display: 'block',
    width: 7,
    borderRadius: 7,
    background: 'linear-gradient(180deg, #dce8ff 0%, #76a4ff 100%)',
  };

  return {
    pageStyle: {
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: p.pageBg,
      color: p.text,
      padding: '34px 20px',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      boxSizing: 'border-box',
    },
    themeToggleWrapStyle: {
      position: 'fixed',
      top: 18,
      right: 18,
      zIndex: 40,
    },
    gridStyle: {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 0,
      backgroundImage: `linear-gradient(${p.gridLine} 1px, transparent 1px),linear-gradient(90deg, ${p.gridLine} 1px, transparent 1px)`,
      backgroundSize: '44px 44px',
    },
    ambientGlowStyle: {
      position: 'fixed',
      top: '26%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 760,
      height: 520,
      borderRadius: '50%',
      background: p.glow,
      pointerEvents: 'none',
      zIndex: 0,
    },
    layoutStyle: {
      width: '100%',
      maxWidth: 1060,
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gridTemplateColumns: '1.03fr 420px',
      alignItems: 'center',
      gap: 42,
    },
    authColumnStyle: {
      width: '100%',
      maxWidth: 420,
      justifySelf: 'center',
    },
    infoPanelStyle: {
      border: `1px solid ${p.border}`,
      borderRadius: 24,
      padding: 32,
      background: p.panelBg,
      boxShadow: p.shadow,
      backdropFilter: 'blur(18px)',
    },
    statusPillStyle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 10px',
      borderRadius: 999,
      border: `1px solid ${p.borderStrong}`,
      color: p.accentSoft,
      background: p.accentBg,
      fontSize: 10,
      fontWeight: 700,
      fontFamily: DASH_MONO_FONT,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: 18,
    },
    statusDotStyle: {
      width: 7,
      height: 7,
      borderRadius: 999,
      background: p.accentSoft,
      boxShadow: '0 0 18px rgba(96,165,250,0.8)',
    },
    heroTitleStyle: {
      margin: 0,
      maxWidth: 560,
      color: p.textHi,
      fontSize: 'clamp(30px, 2.25vw, 40px)',
      lineHeight: 1.08,
      letterSpacing: '-0.035em',
      fontWeight: 500,
      fontFamily: DASH_DISPLAY_FONT,
    },
    heroTextStyle: {
      marginTop: 16,
      maxWidth: 640,
      color: p.textMid,
      fontSize: 13,
      lineHeight: 1.65,
      fontWeight: 400,
      fontFamily: DASH_BODY_FONT,
    },
    moduleGridStyle: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 11,
    },
    moduleCardStyle: {
      display: 'flex',
      gap: 11,
      padding: 12,
      borderRadius: 15,
      background: p.moduleBg,
      border: `1px solid ${p.border}`,
    },
    moduleIconStyle: {
      width: 30,
      height: 30,
      minWidth: 30,
      borderRadius: 9,
      display: 'grid',
      placeItems: 'center',
      color: p.accentSoft,
      background: p.accentBgStrong,
      border: `1px solid ${p.border}`,
    },
    moduleTitleStyle: {
      color: p.textHi,
      fontSize: 13,
      fontWeight: 700,
      marginBottom: 4,
      letterSpacing: '-0.01em',
      fontFamily: DASH_BODY_FONT,
    },
    moduleTextStyle: {
      color: p.textSoft,
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 400,
      fontFamily: DASH_BODY_FONT,
    },
    previewStripStyle: {
      marginTop: 18,
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 10,
    },
    metricStyle: {
      borderRadius: 15,
      padding: '13px 12px',
      background: `linear-gradient(180deg, ${p.accentBgStrong}, ${p.moduleBg})`,
      border: `1px solid ${p.border}`,
    },
    metricValueStyle: {
      color: p.text,
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      fontFamily: DASH_BODY_FONT,
    },
    metricLabelStyle: {
      color: p.textLow,
      fontSize: 10,
      marginTop: 4,
      fontFamily: DASH_MONO_FONT,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
    brandAreaStyle: {
      width: '100%',
      height: 112,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 18,
      overflow: 'visible',
    },
    logoPlateStyle: {
      width: 278,
      height: 86,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 18,
      background: p.logoBg,
      border: `1px solid ${p.border}`,
      boxShadow: p.cardShadow,
      overflow: 'hidden',
    },
    codeBrandStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      width: '100%',
      height: '100%',
      padding: '0 22px',
      userSelect: 'none',
    },
    codeMarkStyle: {
      position: 'relative',
      width: 48,
      height: 52,
      flex: '0 0 auto',
      filter: 'drop-shadow(0 12px 22px rgba(37,99,235,0.24))',
    },
    codeMarkTopStyle: {
      position: 'absolute',
      top: 4,
      left: 10,
      width: 30,
      height: 17,
      borderRadius: 14,
      background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 54%, #1d4ed8 100%)',
      transform: 'rotate(-31deg)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
    },
    codeMarkBottomStyle: {
      position: 'absolute',
      left: 8,
      bottom: 6,
      width: 32,
      height: 18,
      borderRadius: 14,
      background: 'linear-gradient(135deg, #2563eb 0%, #0ea5e9 48%, #38bdf8 100%)',
      transform: 'rotate(31deg)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.20)',
    },
    codeMarkShineStyle: {
      position: 'absolute',
      top: 16,
      left: 16,
      width: 17,
      height: 17,
      borderRadius: 12,
      background: 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0))',
      transform: 'rotate(38deg)',
    },
    codeWordWrapStyle: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 0,
    },
    codeWordStyle: {
      fontFamily: DASH_BODY_FONT,
      fontSize: 33,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: '-0.045em',
      color: p.textHi,
    },
    codeIQStyle: {
      color: p.accent,
      letterSpacing: '-0.08em',
    },
    codeSubtitleStyle: {
      marginTop: 7,
      fontFamily: DASH_MONO_FONT,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.20em',
      textTransform: 'uppercase',
      color: p.accent,
      whiteSpace: 'nowrap',
    },
    logoImageStyle: {
      display: 'block',
      width: 210,
      maxWidth: 210,
      height: 'auto',
      objectFit: 'contain',
      objectPosition: 'center',
      border: 0,
      outline: 0,
      background: 'transparent',
      userSelect: 'none',
      WebkitUserDrag: 'none',
    },
    cardStyle: {
      background: p.cardBg,
      border: `1px solid ${p.border}`,
      borderRadius: 18,
      padding: '30px 28px 26px',
      backdropFilter: 'blur(20px)',
      boxShadow: p.cardShadow,
    },
    eyebrowStyle: {
      color: p.accent,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: 10,
      fontWeight: 700,
      fontFamily: DASH_MONO_FONT,
      marginBottom: 8,
    },
    titleStyle: {
      fontSize: 32,
      lineHeight: 1.08,
      fontWeight: 500,
      color: p.textHi,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.035em',
      marginBottom: 8,
    },
    subtitleStyle: {
      color: p.textSoft,
      fontSize: 13,
      lineHeight: 1.55,
      marginBottom: 20,
      fontFamily: DASH_BODY_FONT,
    },
    errorStyle: {
      background: p.errorBg,
      border: `1px solid ${p.errorBorder}`,
      color: p.error,
      padding: '9px 13px',
      borderRadius: 9,
      marginBottom: 18,
      fontSize: 11.5,
      lineHeight: 1.5,
      fontFamily: DASH_BODY_FONT,
    },
    verificationStyle: {
      marginBottom: 18,
      borderRadius: 12,
      border: `1px solid ${p.borderStrong}`,
      background: p.accentBg,
      padding: '14px 16px',
    },
    verificationHeaderStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 12.5,
      fontWeight: 800,
      color: p.text,
      marginBottom: 7,
      fontFamily: DASH_DISPLAY_FONT,
    },
    verificationTextStyle: {
      fontSize: 12.5,
      lineHeight: 1.6,
      color: p.accentSoft,
      fontFamily: DASH_BODY_FONT,
    },
    verificationActionsStyle: {
      marginTop: 11,
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
    },
    secondaryButtonStyle: {
      border: `1px solid ${p.borderStrong}`,
      borderRadius: 9,
      padding: '9px 12px',
      background: p.accentBgStrong,
      color: p.textHi,
      fontSize: 12.5,
      fontWeight: 700,
      fontFamily: DASH_BODY_FONT,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      cursor: 'pointer',
    },
    supportLinkStyle: {
      color: p.accentSoft,
      fontSize: 12.5,
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      fontFamily: DASH_BODY_FONT,
    },
    formStyle: {
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
    },
    labelStyle: {
      display: 'block',
      marginBottom: 5,
      fontSize: 10,
      fontWeight: 700,
      color: p.textLow,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontFamily: DASH_MONO_FONT,
    },
    inputStyle: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '9px 12px',
      height: 42,
      background: p.inputBg,
      border: `1px solid ${p.borderStrong}`,
      borderColor: p.borderStrong,
      borderRadius: 9,
      color: p.text,
      fontSize: 13,
      fontFamily: DASH_BODY_FONT,
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    },
    buttonStyle: {
      marginTop: 4,
      width: '100%',
      padding: '11px 0',
      fontSize: 13,
      fontWeight: 700,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.01em',
      border: 'none',
      borderRadius: 10,
      color: '#fff',
      transition: 'all 0.18s ease',
    },
    createAccountStyle: {
      marginTop: 20,
      textAlign: 'center',
      fontSize: 12.5,
      color: p.textLow,
      fontFamily: DASH_BODY_FONT,
    },
    createAccountLinkStyle: {
      color: p.accent,
      fontWeight: 700,
      textDecoration: 'none',
      fontFamily: DASH_BODY_FONT,
    },
    legalLinksStyle: {
      marginTop: 20,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '3px 12px',
    },
    legalLinkStyle: {
      fontSize: 10,
      color: p.textLow,
      textDecoration: 'none',
      fontFamily: DASH_MONO_FONT,
      letterSpacing: '0.04em',
    },
    securityTextStyle: {
      marginTop: 10,
      textAlign: 'center',
      fontSize: 10,
      color: p.textLow,
      lineHeight: 1.6,
      fontFamily: DASH_MONO_FONT,
    },
    copyrightStyle: {
      marginTop: 10,
      textAlign: 'center',
      fontSize: 9.5,
      color: p.muted,
      fontFamily: DASH_MONO_FONT,
    },
    fallbackBrandStyle: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 13,
    },
    fallbackIconStyle: {
      width: 68,
      height: 68,
      borderRadius: 19,
      background: 'linear-gradient(145deg, #102b83 0%, #142c8f 45%, #183bb5 100%)',
      border: `1px solid ${p.borderStrong}`,
      boxShadow: '0 14px 34px rgba(10,32,120,0.34), inset 0 1px 0 rgba(255,255,255,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
    },
    barOneStyle: { ...barBaseStyle, height: 15, opacity: 0.72, marginTop: 12 },
    barTwoStyle: { ...barBaseStyle, height: 21, opacity: 0.86, marginTop: 6 },
    barThreeStyle: { ...barBaseStyle, height: 29 },
    fallbackTextWrapStyle: { textAlign: 'center' },
    fallbackTitleStyle: {
      fontSize: 28,
      lineHeight: 1,
      fontWeight: 800,
      color: p.text,
      fontFamily: DASH_DISPLAY_FONT,
      letterSpacing: '-0.045em',
    },
    fallbackIQStyle: { color: p.accent },
    fallbackSubtitleStyle: {
      marginTop: 8,
      fontSize: 10,
      color: p.textLow,
      fontFamily: DASH_MONO_FONT,
      letterSpacing: '0.22em',
    },
  };
}
